import React from 'react';

/**
 * <BrandWordmark /> — shared Yabbit VoiceFabric brand lockup.
 *
 * Renders "Yabbit VoiceFabric" with the animated teal-cyan underline
 * beneath "VoiceFabric". Used on the login splash (hero-stacked /
 * hero-inline) and inside the admin sidebar (compact) so the wordmark
 * stays consistent across every touchpoint.
 *
 * Props:
 *   variant   'hero-stacked' | 'hero-inline' | 'compact'
 *             hero-stacked → "Yabbit" over "VoiceFabric" (desktop login)
 *             hero-inline  → "Yabbit VoiceFabric" inline (mobile login)
 *             compact      → "Yabbit VoiceFabric" tiny caption (sidebar)
 *   className extra classes applied to the outer wrapper.
 *   testId    optional data-testid on the root wrapper.
 *
 * Animation is defined in `index.css` under `.brand-voicefabric*` so
 * both variants share the same draw-in + shimmer keyframes.
 */
export const BrandWordmark = ({
  variant = 'hero-stacked',
  className = '',
  testId,
}) => {
  const isCompact = variant === 'compact';
  const isStacked = variant === 'hero-stacked';

  const rootClass = isCompact
    ? 'text-[11px] font-semibold tracking-widest uppercase leading-none'
    : isStacked
      ? 'text-4xl lg:text-5xl font-bold leading-tight tracking-tight'
      : 'text-3xl font-bold leading-tight tracking-tight';

  const yabbitGlow = isCompact
    ? ''
    : isStacked
      ? 'drop-shadow-[0_0_20px_rgba(45,212,191,0.35)]'
      : 'drop-shadow-[0_0_18px_rgba(45,212,191,0.35)]';
  const yabbitClass = `text-[#FFFFFF] ${yabbitGlow}`.trim();

  const vfClass = isCompact
    ? 'brand-voicefabric brand-voicefabric--compact text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-cyan-400'
    : 'brand-voicefabric text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-cyan-400';

  return (
    <h1 className={`${rootClass} ${className}`.trim()} data-testid={testId}>
      <span className={yabbitClass}>Yabbit</span>
      {isStacked ? <br /> : ' '}
      <span className={vfClass}>
        VoiceFabric
        <span className="brand-voicefabric-underline" aria-hidden="true" />
      </span>
    </h1>
  );
};

export default BrandWordmark;
